import { useQuery } from '@tanstack/react-query'
import { ResearchStudyEnrollment } from '../types/researchStudy'
import { researchStudyService } from '../services/researchStudyService'
import { format } from 'date-fns'
import './PatientResearchStudies.css'

interface PatientResearchStudiesProps {
  patientId: string
  onEnroll?: () => void
  onWithdraw?: (enrollment: ResearchStudyEnrollment) => void
}

export function PatientResearchStudies({ patientId, onEnroll, onWithdraw }: PatientResearchStudiesProps) {
  const { data: enrollments, isLoading, error } = useQuery<ResearchStudyEnrollment[]>({
    queryKey: ['patient-research-studies', patientId],
    queryFn: () => researchStudyService.getPatientStudies(patientId),
  })

  if (isLoading) return <div className="patient-research-studies-loading">Loading research studies...</div>
  if (error) return <div className="patient-research-studies-error">Error loading research studies</div>
  if (!enrollments || enrollments.length === 0) {
    return (
      <div className="patient-research-studies-empty">
        <p>No research study enrollments found</p>
        <button className="btn-primary" onClick={onEnroll}>
          Enroll in Study
        </button>
      </div>
    )
  }

  return (
    <div className="patient-research-studies-list">
      {enrollments.map((enrollment) => (
        <div key={enrollment.id} className="study-enrollment-item">
          <div className="enrollment-header">
            <div className="enrollment-info">
              <div className="enrollment-study-name">{enrollment.studyId}</div>
              <div className="enrollment-date">
                Enrolled: {format(new Date(enrollment.enrolledAt), 'MMM dd, yyyy')}
              </div>
              {enrollment.consentObtained && enrollment.consentDate && (
                <div className="enrollment-consent">
                  Consent obtained: {format(new Date(enrollment.consentDate), 'MMM dd, yyyy')}
                </div>
              )}
            </div>
            <div className="enrollment-status">
              <span className={`status-badge status-${enrollment.status}`}>{enrollment.status}</span>
            </div>
          </div>
          {enrollment.withdrawnAt && (
            <div className="enrollment-withdrawal">
              Withdrawn: {format(new Date(enrollment.withdrawnAt), 'MMM dd, yyyy')}
              {enrollment.withdrawnReason && <span> - {enrollment.withdrawnReason}</span>}
            </div>
          )}
          {enrollment.status === 'enrolled' && onWithdraw && (
            <div className="enrollment-actions">
              <button className="btn-danger btn-sm" onClick={() => onWithdraw(enrollment)}>
                Withdraw
              </button>
            </div>
          )}
        </div>
      ))}
      <div className="study-list-actions">
        <button className="btn-primary" onClick={onEnroll}>
          Enroll in Another Study
        </button>
      </div>
    </div>
  )
}
